import { useEffect, useState } from "react";
import { api } from "../api.js";
import { AdminTenantsTab } from "./AdminTenantsTab.js";
import { AdminAnnouncementsTab } from "./AdminAnnouncementsTab.js";

type AdminTab = "tenants" | "announcements";

/**
 * プラットフォーム管理者ダッシュボードのエントリーコンポーネント。
 * `PLATFORM_ADMIN_EMAILS` に含まれるユーザーのみアクセス可（非該当は 404）。
 */
export function AdminApp() {
  const [tab, setTab] = useState<AdminTab>("tenants");
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<{ email: string; name?: string } | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // ログイン状態確認（/tenants/me は全員呼べる）
        const meRes = await api.getMe();
        if (!meRes?.user?.email) {
          window.location.href = "/login";
          return;
        }
        setMe(meRes.user);

        // 管理者権限確認（/admin/tenants を試し打ち → 404 なら権限なし）
        try {
          await api.adminListTenants();
        } catch (e: any) {
          if (String(e.message).includes("404") || String(e.message).includes("Not Found")) {
            setAccessDenied(true);
          }
        }
      } catch {
        window.location.href = "/login";
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#6b6b68" }}>
        Loading...
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <h1 style={{ fontSize: 32, marginBottom: 16 }}>404</h1>
        <p style={{ color: "#6b6b68" }}>Not Found</p>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    } catch {}
    window.location.href = "/login";
  };

  return (
    <div style={{ minHeight: "100vh", background: "#faf9f7", fontFamily: "system-ui, sans-serif" }}>
      <header
        style={{
          background: "#1a1a1a",
          color: "white",
          padding: "14px 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img
            src="https://21050458.fs1.hubspotusercontent-na2.net/hubfs/21050458/Extension%20Spot/Sign%20Extension%20for%20%E3%82%AF%E3%83%A9%E3%82%A6%E3%83%89%E3%82%B5%E3%82%A4%E3%83%B3.png"
            alt="Sign Extension for クラウドサイン"
            style={{ height: 28, width: "auto", background: "white", padding: "4px 8px", borderRadius: 4 }}
          />
          <span
            style={{
              background: "#d32f2f",
              color: "white",
              padding: "3px 10px",
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.5,
            }}
          >
            PLATFORM ADMIN
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ fontSize: 13, color: "#c9c9c9" }}>{me?.email}</span>
          <button
            onClick={handleLogout}
            style={{
              background: "transparent",
              color: "white",
              border: "1px solid #555",
              borderRadius: 6,
              padding: "6px 12px",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            ログアウト
          </button>
        </div>
      </header>

      <nav
        style={{
          background: "white",
          borderBottom: "1px solid #e8e7e3",
          padding: "0 28px",
          display: "flex",
          gap: 4,
        }}
      >
        {[
          { key: "tenants", label: "テナント" },
          { key: "announcements", label: "お知らせ" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key as AdminTab)}
            style={{
              background: "transparent",
              border: "none",
              borderBottom: tab === t.key ? "2px solid #534AB7" : "2px solid transparent",
              color: tab === t.key ? "#534AB7" : "#6b6b68",
              fontWeight: tab === t.key ? 600 : 500,
              fontSize: 14,
              padding: "14px 20px",
              cursor: "pointer",
            }}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <main style={{ padding: 28, maxWidth: 1400, margin: "0 auto" }}>
        {tab === "tenants" && <AdminTenantsTab />}
        {tab === "announcements" && <AdminAnnouncementsTab />}
      </main>
    </div>
  );
}
